import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Rating,
  Typography,
  Button,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import EditIcon from "@mui/icons-material/Edit";
import { formatDateToCustomFormat, getDateFromCreatedAt } from "../../../utils/dateUtils";

const ReviewCard = ({ review, isCurrentUser = false, onEdit = null }) => {
  const [helpfulCount, setHelpfulCount] = useState(
    review?.helpfulCount || (review?.id ? (review.id.charCodeAt(0) % 5) : 0)
  );
  const [isHelpful, setIsHelpful] = useState(false);

  if (!review || !review.createdAt) {
    return null;
  }

  const dateTimeStr = formatDateToCustomFormat(getDateFromCreatedAt(review.createdAt));
  const displayName = review.display_name || "Verified Customer";
  const initial = displayName.charAt(0).toUpperCase();

  const handleHelpfulToggle = () => {
    if (isHelpful) {
      setIsHelpful(false);
      setHelpfulCount((prev) => Math.max(0, prev - 1));
    } else {
      setIsHelpful(true);
      setHelpfulCount((prev) => prev + 1);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "14px",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        backgroundColor: "#ffffff",
        p: { xs: 2, sm: 2.75 },
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: "0 6px 22px rgba(7, 94, 84, 0.08)",
          borderColor: "rgba(7, 94, 84, 0.25)",
        },
      }}
    >
      {/* Header: User Info, Badges, Date */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            alt={displayName}
            src={review.photoURL || undefined}
            sx={{
              width: 44,
              height: 44,
              bgcolor: "#075e54",
              color: "#ffffff",
              fontWeight: 700,
              fontSize: "1.1rem",
              boxShadow: "0 2px 8px rgba(7, 94, 84, 0.2)",
            }}
          >
            {initial}
          </Avatar>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.98rem",
                  color: "#1e293b",
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </Typography>

              <Chip
                icon={<VerifiedIcon sx={{ fontSize: "14px !important", color: "#16a34a !important" }} />}
                label="Verified Purchase"
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  backgroundColor: "rgba(22, 163, 74, 0.08)",
                  color: "#15803d",
                  border: "1px solid rgba(22, 163, 74, 0.2)",
                  borderRadius: "6px",
                  "& .MuiChip-label": { px: 0.8 },
                }}
              />

              {isCurrentUser && (
                <Chip
                  label="Your Review"
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    backgroundColor: "rgba(7, 94, 84, 0.1)",
                    color: "#075e54",
                    border: "1px solid rgba(7, 94, 84, 0.3)",
                    borderRadius: "6px",
                    "& .MuiChip-label": { px: 0.8 },
                  }}
                />
              )}
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontSize: "0.78rem",
                display: "block",
                mt: 0.3,
              }}
            >
              Reviewed on {dateTimeStr}
            </Typography>
          </Box>
        </Box>

        {isCurrentUser && onEdit && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon sx={{ fontSize: "0.95rem" }} />}
            onClick={onEdit}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontSize: "0.8rem",
              fontWeight: 600,
              py: 0.4,
              px: 1.2,
              color: "#075e54",
              borderColor: "rgba(7, 94, 84, 0.4)",
              "&:hover": {
                borderColor: "#075e54",
                backgroundColor: "rgba(7, 94, 84, 0.06)",
              },
            }}
          >
            Edit
          </Button>
        )}
      </Box>

      {/* Rating & Headline */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1 }}>
        <Rating
          name="read-only-rating"
          value={Number(review.rating) || 0}
          readOnly
          size="small"
          sx={{
            color: "#faaf00",
            "& .MuiRating-iconFilled": {
              filter: "drop-shadow(0 1px 2px rgba(250, 175, 0, 0.25))",
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: "0.85rem",
            color: "#0f172a",
          }}
        >
          {Number(review.rating).toFixed(1)}
        </Typography>
      </Box>

      {/* Review Headline */}
      {review.headline && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: "1.02rem",
            color: "#0f172a",
            mb: 0.75,
            lineHeight: 1.35,
          }}
        >
          {review.headline}
        </Typography>
      )}

      {/* Review Detail / Description */}
      {review.detail && (
        <Typography
          variant="body2"
          sx={{
            color: "#334155",
            fontSize: "0.93rem",
            lineHeight: 1.65,
            whiteSpace: "pre-line",
            mb: 2,
          }}
        >
          {review.detail}
        </Typography>
      )}

      {/* Helpful Action Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: 1.5,
          borderTop: "1px solid rgba(0, 0, 0, 0.05)",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
            Was this review helpful?
          </Typography>

          <Button
            size="small"
            variant={isHelpful ? "contained" : "outlined"}
            onClick={handleHelpfulToggle}
            startIcon={
              isHelpful ? (
                <ThumbUpIcon sx={{ fontSize: "0.9rem !important" }} />
              ) : (
                <ThumbUpOutlinedIcon sx={{ fontSize: "0.9rem !important" }} />
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              fontSize: "0.75rem",
              fontWeight: 600,
              py: 0.25,
              px: 1.25,
              minHeight: 28,
              backgroundColor: isHelpful ? "#075e54" : "transparent",
              color: isHelpful ? "#ffffff" : "#475569",
              borderColor: isHelpful ? "#075e54" : "#cbd5e1",
              "&:hover": {
                backgroundColor: isHelpful ? "#064e46" : "rgba(7, 94, 84, 0.06)",
                borderColor: "#075e54",
              },
            }}
          >
            {isHelpful ? "Helpful" : "Helpful"} {helpfulCount > 0 && `(${helpfulCount})`}
          </Button>
        </Box>

        {isHelpful && (
          <Typography
            variant="caption"
            sx={{
              color: "#16a34a",
              fontWeight: 600,
              fontSize: "0.76rem",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            Thank you for your feedback!
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default ReviewCard;
